import React, { useCallback } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { View } from 'react-native'
import { tv } from 'tailwind-variants'

import { EditableSetButton } from '@flows-ui/components/EditableSetButton'
import type { StepContentRendererProps } from '@flows-ui/types'
import { isInputElement } from '@flows-ui/utils/isInputElement'
import { BUTTON_IDS } from '@flows/configs/analytics/constants'
import type {
  AnyStepContentElement,
  CheckboxOption,
  EditableSet as EditableSetProps,
} from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import type { Space } from '@ui-kit/types'

const editableSetStyles = tv({
  slots: {
    buttonContainer: 'grow justify-center gap-y-8',
  },
})

export const EditableSet = <
  TProps extends EditableSetProps<any, any, any>,
  TStepContentRendererProps extends StepContentRendererProps<AnyStepContentElement>,
>(
  props: TProps & {
    flowValues: FlowValues
    StepContentRenderer: (
      props: TStepContentRendererProps & { space?: Space }
    ) => React.ReactElement | null
    stepContentRendererProps: TStepContentRendererProps & { space?: Space }
  }
) => {
  const {
    maximumItems = 5,
    name,
    configuration,
    StepContentRenderer,
    stepContentRendererProps,
  } = props

  const { buttonContainer } = editableSetStyles()

  const { control } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  })
  const addItem = useCallback(() => {
    const collectedValues: Record<string, undefined | '' | false> = {}
    configuration.elements.forEach((element) => {
      const isInputField = isInputElement(element)
      if (isInputField && element.type === 'checkbox') {
        element.fields.forEach((field: CheckboxOption) => {
          if ('name' in field && field.name !== undefined) {
            collectedValues[field.name] = false
          }
        })
      }

      if (isInputField && 'name' in element) {
        const initialValue = element.type === 'date' ? undefined : ''
        collectedValues[element.name] = initialValue
      }
    })

    append(collectedValues)
  }, [configuration, append])

  return (
    <View>
      <View>
        {fields.map((fieldValue, index) => {
          return (
            <View key={index.toString()}>
              <StepContentRenderer
                {...stepContentRendererProps}
                elements={configuration.elements.map((el) => {
                  if (el.type === 'checkbox') {
                    return {
                      ...el,
                      fields: el.fields.map((field: CheckboxOption) => {
                        return {
                          ...field,
                          name: `${name}.${index}.${field.name}`,
                        }
                      }),
                    }
                  }
                  if ('name' in el && el.type !== 'customComponent') {
                    return {
                      ...el,
                      name: `${name}.${index}.${el.name}`,
                    }
                  }

                  throw new Error(
                    `Assertion: property name is missing for ${el.type} element in EditableSet`
                  )
                })}
              />
              <EditableSetButton
                title={props.removeItemLabel}
                onPress={() => remove(index)}
                type="remove"
                flowValues={props.flowValues}
                testID={BUTTON_IDS.removeEditableSetItem}
              />
            </View>
          )
        })}
      </View>
      <View className={buttonContainer()}>
        {fields.length < maximumItems && (
          <EditableSetButton
            title={props.addItemLabel}
            onPress={() => addItem()}
            type="add"
            flowValues={props.flowValues}
            testID={BUTTON_IDS.addEditableSetItem}
          />
        )}
      </View>
    </View>
  )
}
